import React from "react";
import styled from "@emotion/styled";
import { useAuth } from "../../hooks/useAuth";
import flowLogo from "../../assets/flow_logo.svg";

const SidebarContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 250px;
  background: linear-gradient(180deg, #2d2d2d 0%, #1a1a1a 100%);
  color: white;
  z-index: 1000;
  transform: ${({ isOpen }) =>
    isOpen ? "translateX(0)" : "translateX(-100%)"};
  transition: transform 0.3s ease-in-out;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    position: fixed;
    transform: translateX(0);
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  }
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #3a3a3a;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const Logo = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: white;
`;

const UserInfo = styled.div`
  padding: 20px;
  border-bottom: 1px solid #3a3a3a;
`;

const UserName = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const UserRole = styled.div`
  font-size: 12px;
  color: #a0aec0;
  text-transform: capitalize;
`;

const Navigation = styled.nav`
  padding: 20px 0;
`;

const NavItem = styled.button<{ active?: boolean }>`
  width: 100%;
  padding: 12px 20px;
  border: none;
  background: ${({ active }) =>
    active ? "rgba(255, 107, 107, 0.2)" : "transparent"};
  color: ${({ active }) => (active ? "#FF8E8E" : "#e2e8f0")};
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
    color: #ff8e8e;
  }
`;

const NavIcon = styled.span`
  font-size: 16px;
  width: 20px;
  display: flex;
  justify-content: center;
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};

  @media (min-width: 768px) {
    display: none;
  }
`;

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  activeTab,
  onTabChange,
}) => {
  const { user } = useAuth();

  const handleNavItemClick = (tab: string) => {
    onTabChange(tab);
    onClose(); // Close sidebar on mobile after selection
  };

  const getNavigationItems = () => {
    const items = [];

    // Dashboard is available for all roles
    items.push({
      id: "dashboard",
      label: "Dashboard",
      icon: "📊",
    });

    // Admin-only navigation
    if (user?.role === "admin") {
      items.push({
        id: "customers",
        label: "Customers",
        icon: "👥",
      });
    }

    // Customer-only navigation
    if (user?.role === "customer") {
      items.push({
        id: "leads",
        label: "Leads",
        icon: "🎯",
      });
      items.push({
        id: "pages",
        label: "Landing Pages",
        icon: "📄",
      });
      items.push({
        id: "pages-v2",
        label: "Page Builder",
        icon: "🎨",
      });
      items.push({
        id: "ai-builder",
        label: "AI Builder",
        icon: "🤖",
      });
    }

    // Member navigation (if needed)
    if (user?.role === "member") {
      items.push({
        id: "leads",
        label: "Leads",
        icon: "🎯",
      });
    }

    return items;
  };

  return (
    <>
      <Overlay isOpen={isOpen} onClick={onClose} />
      <SidebarContainer isOpen={isOpen}>
        <SidebarHeader>
          <LogoImage src={flowLogo} alt="Flow Logo" />
          <Logo>Flow</Logo>
        </SidebarHeader>

        <UserInfo>
          <UserName>{user?.name}</UserName>
          <UserRole>{user?.role}</UserRole>
        </UserInfo>

        <Navigation>
          {getNavigationItems().map((item) => (
            <NavItem
              key={item.id}
              active={activeTab === item.id}
              onClick={() => handleNavItemClick(item.id)}
            >
              <NavIcon>{item.icon}</NavIcon>
              {item.label}
            </NavItem>
          ))}
        </Navigation>
      </SidebarContainer>
    </>
  );
};
